import { useState } from "react";
import { apiGoogleConfig } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { takeAfterAuth } from "@/components/CouponOfDay";

const randDigit = () => Math.floor(Math.random() * 9) + 1;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const GOOGLE_STATE_KEY = "readoo_google_state";
export const googleRedirectUri = () => window.location.origin + "/auth/google";

async function startGoogle() {
  let clientId = null;
  try { clientId = (await apiGoogleConfig()).client_id; } catch {}
  if (!clientId) {
    // Progetto Google di Readoo non configurato: flusso Emergent
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    return;
  }
  const state = crypto.getRandomValues(new Uint32Array(4)).join("-");
  sessionStorage.setItem(GOOGLE_STATE_KEY, state);
  const q = new URLSearchParams({
    client_id: clientId, redirect_uri: googleRedirectUri(), response_type: "code",
    scope: "openid email profile", state, prompt: "select_account",
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${q}`;
}

export function GoogleButton({ beforeStart }) {
  return (
    <button
      type="button"
      data-testid="google-login-button"
      onClick={() => { if (!beforeStart || beforeStart() !== false) startGoogle(); }}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-hair bg-surface hover:bg-surfaceTertiary/50 text-onSurface font-semibold text-sm transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
      Continua con Google
    </button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ a: randDigit(), b: randDigit() });
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const newCaptcha = () => { setCaptcha({ a: randDigit(), b: randDigit() }); setCaptchaAnswer(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (parseInt(captchaAnswer, 10) !== captcha.a + captcha.b) {
      toast.error("Verifica anti-bot errata. Riprova.");
      newCaptcha();
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bentornato!");
      navigate(takeAfterAuth() || "/");
    } catch (err) {
      toast.error(err.message || "Credenziali non valide");
      newCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[440px] mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-baseline gap-0.5 mb-3">
          <span className="font-display text-3xl font-bold text-onSurface">Readoo</span>
          <span className="w-2 h-2 rounded-full bg-brand mb-1.5" />
        </div>
        <h1 className="font-serif text-2xl font-semibold text-onSurface">Accedi</h1>
        <p data-testid="login-tagline" className="text-sm text-subtle mt-1">Ti informa. Ti coinvolge. Ti premia.</p>
      </div>

      <div className="bg-surfaceSecondary border border-hair rounded-2xl p-6 sm:p-8">
        <GoogleButton />
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-hair" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-subtle">oppure</span>
          <div className="flex-1 h-px bg-hair" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field icon={Mail} type="email" placeholder="La tua email" value={email} onChange={setEmail} testid="login-email" />
          <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="login-password" />
          <div className="flex justify-end -mt-2">
            <Link to="/password-dimenticata" data-testid="login-forgot-password" className="text-xs font-semibold text-brand hover:underline">Hai dimenticato la password?</Link>
          </div>

          <div data-testid="login-captcha" className="pt-1">
            <p className="text-[11px] font-mono uppercase tracking-wider text-subtle mb-2 flex items-center gap-1.5">
              <ShieldCheck size={13} className="text-brand" /> Verifica anti-bot
            </p>
            <div className="flex items-center gap-2">
              <span data-testid="login-captcha-question" className="text-sm font-medium text-onSurface whitespace-nowrap shrink-0">
                Quanto fa {captcha.a} + {captcha.b}?
              </span>
              <input
                data-testid="login-captcha-input"
                type="text"
                inputMode="numeric"
                placeholder="Risposta"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^0-9]/g, ""))}
                required
                className="flex-1 min-w-0 bg-surface border border-hair rounded-full px-4 py-2.5 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand transition-colors"
              />
              <button
                type="button"
                data-testid="login-captcha-refresh"
                onClick={newCaptcha}
                aria-label="Cambia domanda"
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-hair bg-surface text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-brand text-onBrand font-semibold text-sm hover:bg-brandSecondary disabled:opacity-60 transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Accedi"}
          </button>
        </form>
      </div>

      <p className="text-center text-sm text-subtle mt-6">
        Non hai un account? <Link to="/registrati" data-testid="go-register" className="text-brand font-semibold hover:underline">Registrati</Link>
      </p>
    </div>
  );
}

export function Field({ icon: Icon, type, placeholder, value, onChange, testid, autoComplete }) {
  return (
    <div className="relative">
      <Icon size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-subtle" />
      <input
        data-testid={testid}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete || (type === "email" ? "email" : type === "password" ? "current-password" : undefined)}
        className="w-full bg-surface border border-hair rounded-full pl-11 pr-4 py-3 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand transition-colors"
      />
    </div>
  );
}
